import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faBriefcase,
  faChevronRight,
  faPipe,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { scopeMapper } from '~/helpers/scopeMap';
import { CampaignProps } from '~/types/Campaign';
import { cn } from '../helpers/utils';

type BreadcrumbLink = {
  name: string;
  linkTo: string;
  inNetwork?: boolean;
};

export interface BreadcrumbsProps {
  links?: BreadcrumbLink[];
  variant?: 'default' | 'inverted';
  campaign?: CampaignProps;
}

const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  links,
  variant,
  campaign,
}) => {
  const scopeBreadcrumbs = Object.entries(scopeMapper).map((m) =>
    m[1].officeBreadcrumb.toLowerCase().replace(' ', '')
  );

  const path =
    links && !campaign?.isInternal && campaign?.campaignCode
      ? links.filter(({ name }) => {
          name = name.toLowerCase().replace(' ', '');
          return name !== 'home' && !scopeBreadcrumbs.includes(name);
        })
      : links;

  return (
    <ul className="breadcrumbs items-center">
      {path
        ?.filter(
          (f) =>
            !campaign ||
            !campaign?.isInternal ||
            (campaign?.isInternal && !f.inNetwork)
        )
        .map((link, index) => (
          <Breadcrumb
            key={`${link.linkTo}-${index}`}
            index={index}
            total={path.length}
            link={link}
            variant={variant}
          />
        ))}

      <BreadcrumbsInNetwork
        links={path}
        variant={variant}
        campaign={campaign}
      />
    </ul>
  );
};

const BreadcrumbsInNetwork: React.FC<BreadcrumbsProps> = ({
  links,
  variant,
  campaign,
}) => {
  var filtered = (links ?? []).filter(
    (f) => campaign?.isInternal && f.inNetwork
  );

  if (filtered.length == 0 || !campaign) {
    return null;
  }

  return (
    <li
      className="flex gap-2 rounded-lg border-2 border-blue-700 px-4"
      key="in-network"
    >
      <ul className="my-0 flex items-center">
        {filtered.map((link, index) => (
          <Breadcrumb
            key={`${link.linkTo}-${index}`}
            index={index}
            total={filtered.length}
            link={link}
            variant={variant}
          />
        ))}
        <li>
          {campaign.company ? (
            <a
              className="flex items-center gap-2 py-2 text-xs font-bold text-blue-700 no-underline hover:text-blue-800 md:text-base lg:gap-3"
              href={`/over/aanbieder/${campaign.company?.slug}`}
            >
              <BreadcrumbIcon icon={faPipe as IconProp} variant={variant} />
              <FontAwesomeIcon
                icon={faBriefcase as IconProp}
                className={'text-xs lg:text-sm'}
              />
              {`Binnen het netwerk van ${campaign.name}`}
            </a>
          ) : (
            <span className="flex items-center gap-2 py-2 text-xs font-bold text-blue-700 md:text-base lg:gap-3">
              <BreadcrumbIcon icon={faPipe as IconProp} variant={variant} />
              <FontAwesomeIcon
                icon={faBriefcase as IconProp}
                className={'text-xs lg:text-sm'}
              />
              {`Binnen het netwerk van ${campaign.name}`}
            </span>
          )}
        </li>
      </ul>
    </li>
  );
};

export interface BreadcrumbProps {
  index: number;
  total: number;
  link: BreadcrumbLink;
  variant?: 'default' | 'inverted';
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  index,
  link,
  variant,
  total,
}) => {
  return (
    <li className="mr-2 lg:mr-3">
      <a
        className={cn(
          'flex items-center gap-2 py-2 text-xs font-bold no-underline hover:underline md:text-base lg:gap-3',
          variant === 'inverted'
            ? 'text-white drop-shadow-sm hover:text-white/75'
            : 'text-black hover:text-blue-600'
        )}
        href={link.linkTo?.toLowerCase()}
      >
        {link.name}
        {index !== total - 1 && (
          <BreadcrumbIcon icon={faChevronRight as IconProp} variant={variant} />
        )}
      </a>
    </li>
  );
};

export interface BreadcrumbIconProps {
  icon: IconProp;
  variant?: 'default' | 'inverted';
}

const BreadcrumbIcon: React.FC<BreadcrumbIconProps> = ({ icon, variant }) => {
  return (
    <FontAwesomeIcon
      icon={icon}
      className={cn(
        'text-xs lg:text-sm',
        variant === 'inverted' ? 'text-white/50' : 'text-black'
      )}
    />
  );
};

export default Breadcrumbs;
